<template>
    <v-dialog
        v-model="authDialogOpen"
        max-width="480"
        :scrim="dialogScrim"
        aria-labelledby="auth-dialog-title"
    >
        <v-card class="auth-dialog__card" :class="{ 'auth-dialog__card--dark': isDarkTheme }" @keydown.tab="trapDialogFocus">
            <button
                type="button"
                class="auth-dialog__close"
                aria-label="Close sign in dialog"
                @click="promptStore.closeDialog()"
            >
                <v-icon :icon="mdiClose" size="20" />
            </button>

            <header class="auth-dialog__heading">
                <div class="auth-dialog__brand">
                    <img :src="brandWordmark" alt="AnimeBookmark" width="892" height="136">
                </div>
                <span class="auth-dialog__monogram" aria-hidden="true">
                    <img :src="brandMonogram" alt="" width="531" height="669">
                </span>
                <h2 id="auth-dialog-title" class="auth-dialog__title">{{ dialogTitle }}</h2>
                <p class="auth-dialog__copy">{{ dialogCopy }}</p>
            </header>

            <div class="auth-dialog__body">
                <v-alert
                    v-if="formMessage"
                    :type="formMessageType"
                    variant="tonal"
                    density="comfortable"
                    class="auth-dialog__message"
                    role="status"
                >
                    {{ formMessage }}
                </v-alert>

                <div v-if="showGoogleProvider && !awaitingEmailVerification" class="auth-dialog__google">
                    <CoreGoogleLogin
                        mode="drawer"
                        provider-only
                        provider-label="Continue with Google"
                        :stay-on-current-route-on-login="stayOnCurrentRouteOnLogin"
                        :auth-surface="authSurface"
                    />
                </div>
                <div v-if="showGoogleProvider && !awaitingEmailVerification" class="auth-dialog__divider">
                    <span></span>
                    <span>or use your email</span>
                    <span></span>
                </div>

                <form
                    v-if="!awaitingEmailVerification"
                    class="auth-dialog__form"
                    :aria-busy="isSubmitting"
                    @submit.prevent="submitPasswordAuth"
                >
                    <div v-if="showEmailField" class="auth-dialog__field">
                        <label for="auth-email">Email</label>
                        <input
                            id="auth-email"
                            v-model.trim="email"
                            type="email"
                            autocomplete="email"
                            placeholder="you@example.com"
                            :disabled="isSubmitting"
                            required
                        >
                    </div>
                    <div v-if="showPasswordField" class="auth-dialog__field">
                        <label for="auth-password">Password</label>
                        <div class="auth-dialog__password">
                            <input
                                id="auth-password"
                                v-model="password"
                                :type="showPassword ? 'text' : 'password'"
                                :autocomplete="passwordAutocomplete"
                                :placeholder="isRegisterMode || isResetConfirmMode ? 'At least 8 characters' : 'Enter your password'"
                                :disabled="isSubmitting"
                                required
                            >
                            <button
                                type="button"
                                class="auth-dialog__visibility"
                                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                                :aria-pressed="showPassword"
                                :disabled="isSubmitting"
                                @click="showPassword = !showPassword"
                            >
                                <v-icon :icon="showPassword ? mdiEyeOffOutline : mdiEyeOutline" size="19" />
                            </button>
                        </div>
                    </div>
                    <div v-if="showForgotPassword" class="auth-dialog__forgot-row">
                        <button type="button" class="auth-dialog__forgot" :disabled="isSubmitting" @click="setMode('reset-request')">
                            Forgot password?
                        </button>
                    </div>
                    <div v-if="showConfirmPasswordField" class="auth-dialog__field">
                        <label for="auth-confirm-password">Confirm password</label>
                        <input
                            id="auth-confirm-password"
                            v-model="confirmPassword"
                            :type="showPassword ? 'text' : 'password'"
                            autocomplete="new-password"
                            placeholder="Enter your password again"
                            :disabled="isSubmitting"
                            required
                        >
                    </div>

                    <section v-if="isRegisterMode" class="auth-dialog__avatar-step" aria-label="Optional profile image">
                        <button
                            type="button"
                            class="auth-dialog__avatar-toggle"
                            :aria-expanded="showRegistrationAvatarPicker ? 'true' : 'false'"
                            @click="toggleRegistrationAvatarPicker"
                        >
                            Select a profile image (optional)
                        </button>

                        <div v-if="showRegistrationAvatarPicker" class="auth-dialog__avatar-panel">
                            <button
                                type="button"
                                :class="['auth-dialog__avatar-initials', { 'auth-dialog__avatar-initials--active': !selectedAvatarCharacterId }]"
                                :aria-pressed="!selectedAvatarCharacterId"
                                @click="clearAvatarSelection"
                            >
                                <span>{{ avatarInitials }}</span>
                                <small>Use initials</small>
                            </button>

                            <div v-if="avatarOptionsLoading" class="auth-dialog__avatar-status">
                                Loading profile images...
                            </div>
                            <div v-else-if="avatarOptionGroups.length > 0" class="auth-dialog__avatar-groups">
                                <section
                                    v-for="group in avatarOptionGroups"
                                    :key="group.animeId"
                                    class="auth-dialog__avatar-group"
                                >
                                    <p>{{ group.title }}</p>
                                    <div class="auth-dialog__avatar-grid">
                                        <button
                                            v-for="character in group.characters"
                                            :key="character.id"
                                            type="button"
                                            :class="['auth-dialog__avatar-option', { 'auth-dialog__avatar-option--active': isAvatarSelected(character) }]"
                                            :aria-pressed="isAvatarSelected(character)"
                                            @click="selectAvatarCharacter(character.characterId)"
                                        >
                                            <img :src="character.image" :alt="`${character.name} profile option`" loading="lazy" decoding="async">
                                            <span>{{ character.name }}</span>
                                        </button>
                                    </div>
                                </section>
                            </div>
                            <div v-else class="auth-dialog__avatar-status">
                                Character images are not available right now. Your initials will be used.
                            </div>
                        </div>
                    </section>

                    <v-btn
                        type="submit"
                        variant="flat"
                        block
                        class="auth-dialog__submit"
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                    >
                        {{ submitLabel }}
                        <v-icon :icon="mdiArrowTopRight" size="17" class="auth-dialog__submit-arrow" aria-hidden="true" />
                    </v-btn>
                </form>

                <p v-if="showSecondaryAction" class="auth-dialog__switch">
                    <span v-if="isLoginMode">New to AnimeBookmark? </span>
                    <span v-else-if="isRegisterMode">Already have an account? </span>
                    <button type="button" class="auth-dialog__secondary" :disabled="isSubmitting" @click="toggleMode">
                        {{ secondaryActionLabel }}
                    </button>
                </p>
            </div>

            <footer class="auth-dialog__footer">
                <span aria-hidden="true"></span>
                Discover. Bookmark. Come back for more.
            </footer>
        </v-card>
    </v-dialog>
</template>
<script>
import { mdiArrowTopRight, mdiClose, mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js';
import { computed, nextTick } from 'vue';
import { useTheme } from 'vuetify';
import { storeToRefs } from 'pinia';
import CoreGoogleLogin from './CoreGoogleLogin.vue';
import brandWordmarkLight from '../../assets/branding/editorial-ligature/animebookmark-editorial-wordmark-light.png';
import brandWordmarkDark from '../../assets/branding/editorial-ligature/animebookmark-editorial-wordmark-dark.png';
import brandMonogramLight from '../../assets/branding/editorial-ligature/animebookmark-editorial-icon-light.png';
import brandMonogramDark from '../../assets/branding/editorial-ligature/animebookmark-editorial-icon-dark.png';
import {
    getRegistrationAvatarOptionsRequest,
    loginWithPasswordRequest,
    registerExtensionSignupWithPasswordRequest,
    registerWithPasswordRequest,
    requestPasswordResetRequest,
    resetPasswordRequest,
    verifyExtensionSignupEmailRequest,
    verifyUserEmailRequest
} from '../services/apiServices';
import { createAnalyticsEventId, getAnalyticsBrowserContext, trackAdvertisingStandardEvent, trackGoogleAnalyticsSignupEvent } from '../services/analytics';
import { getPostLoginLandingPath, usePreferences } from '../store/preferences.store';
import { useAuthPrompt } from '../store/authPrompt.store';
import { useNotification } from '../store/notification.store';
import { useUser } from '../store/user.store';
import { normalizeAvatarOptionGroups } from '../utils/avatarOptions';

export default {
    name: 'CoreBookmarkLoginDialog',
    components: {
        CoreGoogleLogin
    },
    data() {
        return {
            mode: 'login',
            email: '',
            password: '',
            confirmPassword: '',
            showPassword: false,
            resetPasswordToken: '',
            avatarOptionGroups: [],
            avatarOptionsLoading: false,
            avatarOptionsLoaded: false,
            selectedAvatarCharacterId: null,
            showRegistrationAvatarPicker: false,
            isSubmitting: false,
            formMessage: '',
            formMessageType: 'info',
            awaitingEmailVerification: false
        };
    },
    computed: {
        brandWordmark() {
            return this.isDarkTheme ? brandWordmarkDark : brandWordmarkLight;
        },
        brandMonogram() {
            return this.isDarkTheme ? brandMonogramDark : brandMonogramLight;
        },
        isBookmarkPrompt() {
            return this.dialogContext === 'bookmark';
        },
        isProgressPrompt() {
            return ['progress', 'watch-progress'].includes(this.dialogContext);
        },
        isSubscriptionPrompt() {
            return this.dialogContext === 'subscription';
        },
        isLoginMode() {
            return this.mode === 'login';
        },
        isRegisterMode() {
            return this.mode === 'register';
        },
        isResetRequestMode() {
            return this.mode === 'reset-request';
        },
        isResetConfirmMode() {
            return this.mode === 'reset-confirm';
        },
        isExtensionSignupFlow() {
            return this.$route?.name === 'ExtensionSignupView'
                || this.dialogContext === 'extension-signup';
        },
        authSurface() {
            return this.isExtensionSignupFlow ? 'extension-signup' : 'default';
        },
        showSecondaryAction() {
            return !this.isExtensionSignupFlow;
        },
        showForgotPassword() {
            return this.isLoginMode && !this.isExtensionSignupFlow;
        },
        showEmailField() {
            return !this.isResetConfirmMode;
        },
        showPasswordField() {
            return !this.isResetRequestMode;
        },
        showConfirmPasswordField() {
            return this.isRegisterMode || this.isResetConfirmMode;
        },
        showGoogleProvider() {
            return this.isLoginMode || this.isRegisterMode;
        },
        passwordAutocomplete() {
            if (this.isRegisterMode || this.isResetConfirmMode) {
                return 'new-password';
            }
            return 'current-password';
        },
        dialogTitle() {
            if (this.isResetConfirmMode) {
                return 'Choose a new password';
            }
            if (this.isResetRequestMode) {
                return 'Reset your password';
            }
            return this.isRegisterMode ? 'Make room for your favorites.' : 'Welcome back.';
        },
        dialogCopy() {
            if (this.isExtensionSignupFlow) {
                return 'Your anime and episode progress, together with AnimeBookmark Episode Tracker.';
            }
            if (this.isResetConfirmMode) {
                return 'Enter a new password for your AnimeBookmark account.';
            }
            if (this.isResetRequestMode) {
                return 'Enter your email and we will send a secure password reset link if the account exists.';
            }
            if (this.isBookmarkPrompt) {
                return 'Use Google or email to keep your bookmarks synced across devices.';
            }
            if (this.isProgressPrompt) {
                return 'Sign in once and AnimeBookmark will remember where you left off.';
            }
            if (this.isSubscriptionPrompt) {
                return 'Continue to Premium with one account across your devices.';
            }
            return this.isRegisterMode
                ? 'A little space for every anime you love.'
                : 'Your anime, right where you left it.';
        },
        submitLabel() {
            if (this.isResetConfirmMode) {
                return 'Reset password';
            }
            if (this.isResetRequestMode) {
                return 'Send reset link';
            }
            return this.isRegisterMode ? 'Create account' : 'Sign in';
        },
        secondaryActionLabel() {
            if (this.isResetRequestMode || this.isResetConfirmMode) {
                return 'Back to sign in';
            }
            return this.isRegisterMode ? 'Sign in' : 'Create account';
        },
        avatarInitials() {
            const source = `${this.email || ''}`.trim();
            const normalizedSource = source.includes('@') ? source.split('@')[0] : source;
            const nameParts = normalizedSource
                .split(/\s+/)
                .map((part) => part.replace(/[^A-Za-z0-9]/g, ''))
                .filter(Boolean);
            const initials = nameParts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
            return initials || 'A';
        }
    },
    methods: {
        trapDialogFocus(event) {
            if (event.altKey || event.ctrlKey || event.metaKey) return;
            // Vuetify restores focus when it enters another page element, but a
            // Tab from the last control can instead reach the browser chrome.
            const controls = Array.from(event.currentTarget.querySelectorAll(
                'button, input, select, textarea, a[href], iframe, [tabindex]'
            )).filter((element) => element.tabIndex >= 0
                && !element.disabled
                && !element.closest('[inert], [aria-hidden="true"]')
                && element.getClientRects().length > 0
                && window.getComputedStyle(element).visibility !== 'hidden');
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (first && ((event.shiftKey && event.target === first)
                || (!event.shiftKey && event.target === last))) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            }
        },
        normalizeMode(mode) {
            const normalized = `${mode || ''}`.trim().toLowerCase();
            if (['login', 'register', 'reset-request', 'reset-confirm'].includes(normalized)) {
                return normalized;
            }
            return 'login';
        },
        setMode(mode) {
            this.showPassword = false;
            this.mode = this.isExtensionSignupFlow
                ? 'register'
                : this.normalizeMode(mode);
            this.password = '';
            this.confirmPassword = '';
            this.formMessage = '';
            if (!this.isRegisterMode) {
                this.showRegistrationAvatarPicker = false;
                this.selectedAvatarCharacterId = null;
            }
        },
        toggleMode() {
            if (this.isExtensionSignupFlow) {
                this.setMode('register');
                return;
            }
            if (this.isResetRequestMode || this.isResetConfirmMode) {
                this.setMode('login');
                return;
            }
            this.setMode(this.isRegisterMode ? 'login' : 'register');
        },
        validateForm() {
            const normalizedEmail = `${this.email || ''}`.trim();
            if (this.showEmailField && (!normalizedEmail || !normalizedEmail.includes('@'))) {
                return 'Enter a valid email address.';
            }
            if (this.isResetConfirmMode && !this.resetPasswordToken) {
                return 'Password reset link is missing or invalid.';
            }
            if (this.isLoginMode && !this.password) {
                return 'Enter your password.';
            }
            if (!this.isLoginMode && this.showPasswordField && (!this.password || this.password.length < 8)) {
                return 'Password must be at least 8 characters.';
            }
            if (this.showPasswordField && this.password.length > 128) {
                return 'Password must be 128 characters or fewer.';
            }
            if (this.showConfirmPasswordField && this.password !== this.confirmPassword) {
                return 'Passwords do not match.';
            }
            return '';
        },
        async routeAfterLogin() {
            if (this.stayOnCurrentRouteOnLogin) {
                return;
            }
            const targetPath = getPostLoginLandingPath(this.postLoginLanding);
            if (!targetPath || targetPath === '/' || this.$route?.path === targetPath) {
                return;
            }
            await this.$router.replace(targetPath);
        },
        trackAuthEvent(authTracking, fallbackEventId, { suppressAuthTracking = false } = {}) {
            if (suppressAuthTracking || this.isExtensionSignupFlow || !authTracking?.eventName) {
                return;
            }
            trackAdvertisingStandardEvent({
                eventName: authTracking.eventName,
                eventId: authTracking.eventId || fallbackEventId,
                properties: {
                    source: this.isRegisterMode ? 'password_registration' : 'password_login'
                }
            });
        },
        trackVerifiedPasswordSignup(sessionData, fallbackEventId, { suppressAuthTracking = false } = {}) {
            if (suppressAuthTracking || this.isExtensionSignupFlow) {
                return;
            }
            if (sessionData?.authTracking?.eventName) {
                return;
            }

            const authProviders = Array.isArray(sessionData?.user?.authProviders)
                ? sessionData.user.authProviders.map((provider) => `${provider || ''}`.trim().toLowerCase())
                : [];
            if (!authProviders.includes('password') || authProviders.includes('google')) {
                return;
            }

            trackGoogleAnalyticsSignupEvent({
                eventId: fallbackEventId,
                method: 'password',
                properties: {
                    source: 'password_email_verification'
                }
            });
        },
        async loadAvatarOptions() {
            if (this.avatarOptionsLoading || this.avatarOptionsLoaded) {
                return;
            }
            this.avatarOptionsLoading = true;
            try {
                const data = await getRegistrationAvatarOptionsRequest();
                this.avatarOptionGroups = normalizeAvatarOptionGroups(data);
                this.avatarOptionsLoaded = true;
            } catch (_err) {
                this.avatarOptionGroups = [];
                this.avatarOptionsLoaded = false;
            } finally {
                this.avatarOptionsLoading = false;
            }
        },
        toggleRegistrationAvatarPicker() {
            this.showRegistrationAvatarPicker = !this.showRegistrationAvatarPicker;
            if (this.showRegistrationAvatarPicker) {
                this.loadAvatarOptions();
            }
        },
        selectAvatarCharacter(characterId) {
            this.selectedAvatarCharacterId = Number.parseInt(characterId, 10) || null;
        },
        clearAvatarSelection() {
            this.selectedAvatarCharacterId = null;
        },
        isAvatarSelected(character = {}) {
            return this.selectedAvatarCharacterId && Number.parseInt(character.characterId, 10) === this.selectedAvatarCharacterId;
        },
        async completeSession(sessionData, authEventId, { suppressAuthTracking = false } = {}) {
            if (!sessionData?.user || !sessionData?.token) {
                throw new Error('Invalid auth response');
            }
            this.trackAuthEvent(sessionData.authTracking, authEventId, { suppressAuthTracking });
            this.userStore.setSession({
                user: sessionData.user,
                token: sessionData.token
            });
            this.promptStore.closeDialog();
            await nextTick();
            await this.routeAfterLogin();
        },
        async submitPasswordAuth() {
            const validationMessage = this.validateForm();
            if (validationMessage) {
                this.formMessageType = 'error';
                this.formMessage = validationMessage;
                return;
            }
            if (this.isSubmitting) {
                return;
            }

            this.isSubmitting = true;
            this.formMessage = '';
            try {
                if (this.isResetRequestMode) {
                    await requestPasswordResetRequest(this.email);
                    this.formMessageType = 'success';
                    this.formMessage = 'If an account exists for that email, a password reset link has been sent.';
                    return;
                }

                if (this.isResetConfirmMode) {
                    const sessionData = await resetPasswordRequest({
                        token: this.resetPasswordToken,
                        password: this.password
                    });
                    await this.completeSession(sessionData, createAnalyticsEventId('auth_reset'));
                    const nextQuery = { ...(this.$route?.query || {}) };
                    delete nextQuery.resetPasswordToken;
                    await this.$router.replace({
                        path: this.$route.path,
                        query: nextQuery
                    });
                    this.notificationState.notify({ message: 'Password reset. You are signed in.' });
                    return;
                }

                const isExtensionSignupRequest = this.isExtensionSignupFlow;
                const authEventId = isExtensionSignupRequest
                    ? ''
                    : createAnalyticsEventId('auth');
                let sessionData = null;
                if (isExtensionSignupRequest) {
                    sessionData = await registerExtensionSignupWithPasswordRequest({
                        email: this.email,
                        password: this.password,
                        avatarCharacterId: this.selectedAvatarCharacterId
                    });
                } else {
                    const browserContext = getAnalyticsBrowserContext();
                    const requestPayload = {
                        email: this.email,
                        password: this.password,
                        avatarCharacterId: this.isRegisterMode ? this.selectedAvatarCharacterId : null,
                        analyticsEventId: authEventId,
                        analyticsSessionId: browserContext.sessionId,
                        redditUuid: browserContext.redditUuid,
                        adAttribution: browserContext.adAttribution,
                        eventSourceUrl: browserContext.eventSourceUrl,
                        pagePath: browserContext.pagePath,
                        screenWidth: browserContext.screenWidth,
                        screenHeight: browserContext.screenHeight
                    };
                    sessionData = this.isRegisterMode
                        ? await registerWithPasswordRequest(requestPayload)
                        : await loginWithPasswordRequest(requestPayload);
                }

                if (sessionData?.requiresEmailVerification) {
                    if (isExtensionSignupRequest) {
                        this.awaitingEmailVerification = true;
                    } else {
                        this.setMode('login');
                    }
                    this.email = sessionData.email || this.email;
                    this.formMessageType = 'success';
                    this.formMessage = isExtensionSignupRequest
                        ? 'Check your email to verify the account. After verification, return to AnimeBookmark Episode Tracker and sign in.'
                        : 'Check your email to verify the account, then sign in.';
                    return;
                }

                await this.completeSession(sessionData, authEventId, {
                    suppressAuthTracking: isExtensionSignupRequest
                });
            } catch (err) {
                this.formMessageType = 'error';
                this.formMessage = err?.response?.data?.message || err?.message || 'Authentication failed. Please try again.';
            } finally {
                this.isSubmitting = false;
            }
        },
        async verifyEmailFromRoute() {
            const token = `${this.$route?.query?.verifyEmailToken || ''}`.trim();
            if (!token) {
                return;
            }
            const isExtensionSignupRequest = this.isExtensionSignupFlow;
            if (isExtensionSignupRequest) {
                this.promptStore.openRegisterDialog({
                    context: 'extension-signup',
                    stayOnCurrentRouteOnLogin: true
                });
            } else {
                this.promptStore.openLoginDialog({
                    context: 'general',
                    stayOnCurrentRouteOnLogin: false
                });
            }
            this.formMessageType = 'info';
            this.formMessage = 'Verifying your email...';
            this.isSubmitting = true;
            try {
                const authEventId = isExtensionSignupRequest
                    ? ''
                    : createAnalyticsEventId('auth_verify');
                let sessionData = null;
                if (isExtensionSignupRequest) {
                    sessionData = await verifyExtensionSignupEmailRequest({ token });
                } else {
                    const browserContext = getAnalyticsBrowserContext();
                    sessionData = await verifyUserEmailRequest({
                        token,
                        analyticsEventId: authEventId,
                        analyticsSessionId: browserContext.sessionId,
                        redditUuid: browserContext.redditUuid,
                        adAttribution: browserContext.adAttribution,
                        eventSourceUrl: browserContext.eventSourceUrl,
                        pagePath: browserContext.pagePath,
                        screenWidth: browserContext.screenWidth,
                        screenHeight: browserContext.screenHeight
                    });
                }
                this.trackVerifiedPasswordSignup(sessionData, authEventId, {
                    suppressAuthTracking: isExtensionSignupRequest
                });
                await this.completeSession(sessionData, authEventId, {
                    suppressAuthTracking: isExtensionSignupRequest
                });
                const nextQuery = { ...(this.$route?.query || {}) };
                delete nextQuery.verifyEmailToken;
                await this.$router.replace({
                    path: this.$route.path,
                    query: nextQuery
                });
                this.notificationState.notify({ message: 'Email verified. You are signed in.' });
            } catch (err) {
                this.formMessageType = 'error';
                this.formMessage = err?.response?.data?.message || err?.message || 'Email verification failed.';
            } finally {
                this.isSubmitting = false;
            }
        },
        resetPasswordFromRoute() {
            const token = `${this.$route?.query?.resetPasswordToken || ''}`.trim();
            if (!token) {
                return;
            }
            this.resetPasswordToken = token;
            this.promptStore.openPasswordResetConfirmDialog({
                context: 'general',
                stayOnCurrentRouteOnLogin: false
            });
        },
        isSignupQueryEnabled(value = '') {
            const rawValue = Array.isArray(value) ? value[0] : value;
            const normalized = `${rawValue || ''}`.trim().toLowerCase();
            return ['1', 'true', 'yes', 'on', 'signup', 'register'].includes(normalized);
        },
        openSignupFromRoute() {
            if (this.isLoggedIn) {
                return;
            }
            if (this.$route?.query?.verifyEmailToken || this.$route?.query?.resetPasswordToken) {
                return;
            }
            const isExtensionSignupRoute = this.$route?.name === 'ExtensionSignupView';
            if (!isExtensionSignupRoute && !this.isSignupQueryEnabled(this.$route?.query?.signup)) {
                return;
            }
            this.promptStore.openRegisterDialog({
                context: isExtensionSignupRoute ? 'extension-signup' : 'general',
                stayOnCurrentRouteOnLogin: isExtensionSignupRoute
            });
        }
    },
    watch: {
        authDialogOpen(value) {
            if (!value) {
                return;
            }
            this.showRegistrationAvatarPicker = false;
            this.selectedAvatarCharacterId = null;
            this.awaitingEmailVerification = false;
            this.setMode(this.initialMode);
        },
        initialMode(value) {
            if (this.authDialogOpen) {
                this.setMode(value);
            }
        },
        isLoggedIn(value) {
            if (value) {
                this.promptStore.closeDialog();
            }
        },
        '$route.query.verifyEmailToken'() {
            this.verifyEmailFromRoute();
        },
        '$route.query.resetPasswordToken'() {
            this.resetPasswordFromRoute();
        },
        '$route.query.signup'() {
            this.openSignupFromRoute();
        },
        '$route.name'() {
            this.openSignupFromRoute();
        }
    },
    mounted() {
        this.verifyEmailFromRoute();
        this.resetPasswordFromRoute();
        this.openSignupFromRoute();
    },
    setup() {
        const promptStore = useAuthPrompt();
        const { dialogOpen: authDialogOpen, dialogContext, initialMode, stayOnCurrentRouteOnLogin } = storeToRefs(promptStore);
        const userStore = useUser();
        const { isLoggedIn } = storeToRefs(userStore);
        const preferencesState = usePreferences();
        const { postLoginLanding } = storeToRefs(preferencesState);
        const notificationState = useNotification();
        const theme = useTheme();
        const isDarkTheme = computed(() => theme.global.current.value.dark);
        const dialogScrim = computed(() => (theme.global.name.value === 'light'
            ? 'rgba(5, 8, 14, 0.24)'
            : 'rgba(6, 10, 18, 0.76)'));

        return {
            promptStore,
            authDialogOpen,
            dialogScrim,
            isDarkTheme,
            dialogContext,
            initialMode,
            stayOnCurrentRouteOnLogin,
            userStore,
            isLoggedIn,
            postLoginLanding,
            notificationState,
            mdiClose,
            mdiArrowTopRight,
            mdiEyeOutline,
            mdiEyeOffOutline
        };
    }
};
</script>

<style scoped>
.auth-dialog__card {
    --auth-paper: #ffffff;
    --auth-soft: #f0f5fb;
    --auth-ink: #172b40;
    --auth-muted: #5d7085;
    --auth-line: #d4dfeb;
    --auth-accent: #2862aa;
    --auth-on-accent: #ffffff;
    --auth-accent-soft: #e7f0fc;
    --auth-shadow: #183a5d18;
    position: relative;
    display: block;
    padding: 29px 39px 0;
    border: 1px solid var(--auth-line);
    border-radius: 25px !important;
    background: var(--auth-paper);
    color: var(--auth-ink);
    box-shadow: 0 20px 50px var(--auth-shadow);
    font-family: 'Segoe UI', Roboto, Arial, sans-serif;
    color-scheme: light;
}

.auth-dialog__card--dark {
    --auth-paper: #1a212b;
    --auth-soft: #222f40;
    --auth-ink: #eff5fc;
    --auth-muted: #adbed0;
    --auth-line: #3d4e61;
    --auth-accent: #8bc0ff;
    --auth-on-accent: #112235;
    --auth-accent-soft: #243c58;
    --auth-shadow: #00000050;
    color-scheme: dark;
}

.auth-dialog__card button,
.auth-dialog__card input {
    font-family: inherit;
}

.auth-dialog__card button {
    transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease;
}

.auth-dialog__card button:focus-visible {
    outline: 2px solid var(--auth-accent);
    outline-offset: 3px;
}

.auth-dialog__close {
    position: absolute;
    top: 15px;
    right: 15px;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    color: var(--auth-muted);
}

.auth-dialog__close:hover {
    background: var(--auth-soft);
    color: var(--auth-ink);
}

.auth-dialog__heading {
    text-align: center;
}

.auth-dialog__brand {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 38px;
    padding-inline: 30px;
}

.auth-dialog__brand img {
    display: block;
    width: 240px;
    max-width: 100%;
    height: auto;
    object-fit: contain;
}

.auth-dialog__monogram {
    display: grid;
    place-items: center;
    width: 61px;
    height: 65px;
    margin: 29px auto 19px;
    border-radius: 20px;
    background: var(--auth-accent-soft);
    color: var(--auth-accent);
}

.auth-dialog__monogram img {
    display: block;
    width: 40px;
    height: 50px;
    object-fit: contain;
}

.auth-dialog__title {
    margin: 0;
    color: var(--auth-ink);
    font-size: 29px;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.9px;
    text-wrap: balance;
}

.auth-dialog__copy {
    margin: 10px 0 0;
    color: var(--auth-muted);
    font-size: 14px;
    line-height: 1.65;
}

.auth-dialog__body {
    margin-top: 25px;
}

.auth-dialog__message {
    margin-bottom: 18px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.6;
}

.auth-dialog__form {
    display: grid;
    gap: 16px;
}

.auth-dialog__field {
    display: grid;
    gap: 8px;
    min-width: 0;
}

.auth-dialog__field label {
    color: var(--auth-ink);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
}

.auth-dialog__field input {
    width: 100%;
    min-width: 0;
    height: 46px;
    padding: 0 13px;
    border: 1px solid var(--auth-line);
    border-radius: 10px;
    outline: none;
    background: var(--auth-paper);
    color: var(--auth-ink);
    font-size: 14px;
    transition: border-color 160ms ease, box-shadow 160ms ease;
}

.auth-dialog__field input::placeholder {
    color: var(--auth-muted);
    opacity: 0.85;
}

.auth-dialog__field input:focus {
    border-color: var(--auth-accent);
    box-shadow: 0 0 0 3px var(--auth-accent-soft);
}

.auth-dialog__password {
    position: relative;
}

.auth-dialog__password input {
    padding-right: 46px;
}

.auth-dialog__visibility {
    position: absolute;
    inset: 1px 1px 1px auto;
    display: grid;
    place-items: center;
    width: 43px;
    border-radius: 0 9px 9px 0;
    color: var(--auth-muted);
}

.auth-dialog__visibility:hover {
    color: var(--auth-accent);
    background: var(--auth-soft);
}

.auth-dialog__forgot-row {
    margin-top: -6px;
    text-align: right;
}

.auth-dialog__forgot,
.auth-dialog__secondary,
.auth-dialog__avatar-toggle {
    color: var(--auth-accent);
    font-size: 12px;
    font-weight: 600;
    line-height: 1.8;
}

.auth-dialog__forgot:hover,
.auth-dialog__secondary:hover,
.auth-dialog__avatar-toggle:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
}

.auth-dialog__submit.v-btn {
    min-height: 46px;
    height: 46px;
    border-radius: 10px;
    background: var(--auth-accent);
    color: var(--auth-on-accent);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    box-shadow: none;
}

.auth-dialog__submit-arrow {
    margin-left: 9px;
    transition: transform 160ms ease;
}

.auth-dialog__submit:hover .auth-dialog__submit-arrow {
    transform: translate(2px, -2px);
}

.auth-dialog__divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 19px 0;
    color: var(--auth-muted);
    font-size: 11px;
}

.auth-dialog__divider span:first-child,
.auth-dialog__divider span:last-child {
    flex: 1;
    height: 1px;
    background: var(--auth-line);
}

.auth-dialog__switch {
    margin: 21px 0 0;
    color: var(--auth-muted);
    text-align: center;
    font-size: 12px;
    line-height: 1.8;
}

.auth-dialog__footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 23px -39px 0;
    padding: 17px 14px;
    border-top: 1px solid var(--auth-line);
    border-radius: 0 0 25px 25px;
    background: var(--auth-soft);
    color: var(--auth-muted);
    text-align: center;
    font-size: 11px;
    line-height: 1.5;
}

.auth-dialog__footer > span {
    flex: 0 0 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--auth-accent);
}

.auth-dialog__avatar-step {
    display: grid;
    gap: 10px;
}

.auth-dialog__avatar-toggle {
    justify-self: start;
    text-align: left;
}

.auth-dialog__avatar-panel {
    display: grid;
    gap: 12px;
    padding: 12px;
    border: 1px solid var(--auth-line);
    border-radius: 10px;
    background: var(--auth-soft);
}

.auth-dialog__avatar-initials,
.auth-dialog__avatar-option {
    border: 1px solid var(--auth-line);
    border-radius: 10px;
    background: var(--auth-paper);
    color: var(--auth-ink);
    cursor: pointer;
}

.auth-dialog__avatar-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 50px;
}

.auth-dialog__avatar-initials span {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--auth-accent);
    color: var(--auth-on-accent);
    font-size: 13px;
    font-weight: 700;
}

.auth-dialog__avatar-initials small {
    font-size: 12px;
    font-weight: 600;
}

.auth-dialog__avatar-group p {
    margin: 0 0 8px;
    color: var(--auth-muted);
    font-size: 12px;
    font-weight: 600;
}

.auth-dialog__avatar-groups {
    display: grid;
    gap: 14px;
    max-height: 220px;
    overflow-y: auto;
    padding: 3px;
    scrollbar-width: thin;
}

.auth-dialog__avatar-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
}

.auth-dialog__avatar-option {
    display: grid;
    justify-items: center;
    gap: 5px;
    min-width: 0;
    padding: 7px 4px;
}

.auth-dialog__avatar-option img {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
}

.auth-dialog__avatar-option span {
    max-width: 100%;
    overflow: hidden;
    color: var(--auth-muted);
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.auth-dialog__avatar-initials--active,
.auth-dialog__avatar-option--active,
.auth-dialog__avatar-initials:hover,
.auth-dialog__avatar-option:hover {
    border-color: var(--auth-accent);
    background: var(--auth-accent-soft);
}

.auth-dialog__avatar-status {
    color: var(--auth-muted);
    font-size: 12px;
    line-height: 1.6;
}

@media (max-width: 520px) {
    .auth-dialog__card {
        padding: 27px 23px 0;
    }

    .auth-dialog__footer {
        margin-inline: -23px;
    }

    .auth-dialog__title {
        font-size: 27px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .auth-dialog__card button,
    .auth-dialog__card input,
    .auth-dialog__submit-arrow {
        transition: none;
    }
}
</style>
